import { configureStore } from '@reduxjs/toolkit';
import completionReducer from './reducer/completionReducer';
import { completionApi } from '../pages/Application/Completion/completionApi';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import newContractReducer from './reducer/newContractReducer';
import { formsApi } from '../pages/Application/formsApi';
import { newContractApi } from '../pages/Application/NewContract/newContractApi';
import { configApi } from '../configApi';
import { adjustmentAidApi } from '../pages/Application/AdjustmenAid/adjustmentAidApi';
import adjustmentAidSlice from './reducer/adjustmentAidSlice';
import { userApi } from '../components/common/User/userApi';
import { csnApplicationApi } from '../pages/Application/CsnApplication/csnApplicationApi';
import csnApplicationReducer from './reducer/csnApplicationReducer';
import { homeApi } from 'components/home/homeApi';
import homeSlice from './reducer/homeSlice';
import { contentApi } from '../pages/Content/ContentApi';
import { previewApi } from '../pages/Preview/PreviewApi';
import { administratorApi } from '../pages/Administration/administrationApi';
import administrationSlice from './reducer/administrationSlice';
import { newsApi } from '../pages/News/NewsApi';
import newsSlice from './reducer/newsSlice';
import { billingApi } from '../pages/Billing/BillingApi';
import billingSlice from './reducer/billingSlice';
import { conversionProjectApi } from '../pages/ConversionProject/ConversionProjectApi';
import conversionProjectSlice from './reducer/conversionProjectSlice';
import { coachApi } from 'components/home/coach/coachApi';
import { notesApi } from 'components/NotesBlock/notesApi';
import { professionApi } from './api/professionApi';
import { reportMeetingApi } from 'components/ReportMeeting/reportMeetingApi';
import { externalAdvisorApi } from 'components/home/externalAdvisor/externalAdvisorApi';
import { frameworkAgreementsApi } from './api/frameworkAgreementsApi';
import { activityApi } from './api/activityApi';
import { contactAttemptsApi } from './api/contactAttemptsApi';
import { interviewInterestApi } from 'components/InterviewInterest/interviewInterestApi';
import { guidanceApi } from 'components/home/guidance/guidanceApi';
import { warningsApi } from 'components/home/warnings/warningsApi';
import { languagesApi } from './api/languagesApi';
import { portalBaseApi } from './api/portalBaseApi';
import authReducer from './reducer/authSlice';
import { bisnodeApi } from './api/BisnodeApi';
import applicationSlice from './reducer/applicationSlice';
import { applicationApi } from '../pages/ApplicationV3/applicationApi';
import { intelligentServicesApi } from './api/intelligentServicesApi';
import { companiesApi } from './api/companiesApi';
import { partyApplicationApi } from '../pages/PartyApplication/partyApplicationApi';
import partyApplicationSlice from './reducer/partyApplicationSlice';

const sanitizeForDevtools = (value: unknown, depth = 0): unknown => {
  // Prevent deep recursion / excessive work for DevTools.
  if (depth > 6) return value;

  if (Array.isArray(value)) {
    return value.map((v) => sanitizeForDevtools(v, depth + 1));
  }

  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;

    // Special-case the ApplicationFile shape: it contains a large base64 string in `file`.
    const fileValue = obj.file;
    if (typeof fileValue === 'string' && fileValue.length > 5000) {
      return {
        ...obj,
        file: `[omitted ${fileValue.length} chars]`,
      };
    }

    const next: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      // Also catch any other accidental large strings.
      if (typeof val === 'string' && val.length > 5000) {
        next[key] = `[omitted ${val.length} chars]`;
      } else {
        next[key] = sanitizeForDevtools(val, depth + 1);
      }
    }
    return next;
  }

  return value;
};

export const store = configureStore({
  reducer: {
    completion: completionReducer,
    [completionApi.reducerPath]: completionApi.reducer,
    newContract: newContractReducer,
    [formsApi.reducerPath]: formsApi.reducer,
    [newContractApi.reducerPath]: newContractApi.reducer,
    [configApi.reducerPath]: configApi.reducer,
    adjustmentAid: adjustmentAidSlice,
    [adjustmentAidApi.reducerPath]: adjustmentAidApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [csnApplicationApi.reducerPath]: csnApplicationApi.reducer,
    csnApplication: csnApplicationReducer,
    [homeApi.reducerPath]: homeApi.reducer,
    home: homeSlice,
    [administratorApi.reducerPath]: administratorApi.reducer,
    administration: administrationSlice,
    [contentApi.reducerPath]: contentApi.reducer,
    [previewApi.reducerPath]: previewApi.reducer,
    [newsApi.reducerPath]: newsApi.reducer,
    newsSlice: newsSlice,
    [billingApi.reducerPath]: billingApi.reducer,
    billing: billingSlice,
    [coachApi.reducerPath]: coachApi.reducer,
    [conversionProjectApi.reducerPath]: conversionProjectApi.reducer,
    conversionProject: conversionProjectSlice,
    [notesApi.reducerPath]: notesApi.reducer,
    [professionApi.reducerPath]: professionApi.reducer,
    [reportMeetingApi.reducerPath]: reportMeetingApi.reducer,
    [externalAdvisorApi.reducerPath]: externalAdvisorApi.reducer,
    [frameworkAgreementsApi.reducerPath]: frameworkAgreementsApi.reducer,
    [activityApi.reducerPath]: activityApi.reducer,
    [contactAttemptsApi.reducerPath]: contactAttemptsApi.reducer,
    [interviewInterestApi.reducerPath]: interviewInterestApi.reducer,
    [guidanceApi.reducerPath]: guidanceApi.reducer,
    [warningsApi.reducerPath]: warningsApi.reducer,
    [languagesApi.reducerPath]: languagesApi.reducer,
    [portalBaseApi.reducerPath]: portalBaseApi.reducer,
    [bisnodeApi.reducerPath]: bisnodeApi.reducer,
    [applicationApi.reducerPath]: applicationApi.reducer,
    application: applicationSlice,
    [partyApplicationApi.reducerPath]: partyApplicationApi.reducer,
    partyApplication: partyApplicationSlice,
    [intelligentServicesApi.reducerPath]: intelligentServicesApi.reducer,
    [companiesApi.reducerPath]: companiesApi.reducer,
    auth: authReducer,
  },
  devTools: import.meta.env.DEV
    ? {
        actionSanitizer: (action: any) => {
          if (!action || typeof action !== 'object') return action;
          if (!('payload' in action)) return action;
          return {
            ...action,
            payload: sanitizeForDevtools((action as any).payload),
          };
        },
        stateSanitizer: (state: any) => {
          if (!state || typeof state !== 'object') return state;
          // Only sanitize the large application slice; leave the rest intact.
          return {
            ...state,
            application: sanitizeForDevtools((state as any).application),
          };
        },
      }
    : false,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      completionApi.middleware,
      formsApi.middleware,
      newContractApi.middleware,
      configApi.middleware,
      adjustmentAidApi.middleware,
      userApi.middleware,
      csnApplicationApi.middleware,
      homeApi.middleware,
      administratorApi.middleware,
      contentApi.middleware,
      previewApi.middleware,
      newsApi.middleware,
      billingApi.middleware,
      coachApi.middleware,
      notesApi.middleware,
      professionApi.middleware,
      reportMeetingApi.middleware,
      conversionProjectApi.middleware,
      externalAdvisorApi.middleware,
      frameworkAgreementsApi.middleware,
      activityApi.middleware,
      contactAttemptsApi.middleware,
      interviewInterestApi.middleware,
      guidanceApi.middleware,
      warningsApi.middleware,
      languagesApi.middleware,
      portalBaseApi.middleware,
      bisnodeApi.middleware,
      applicationApi.middleware,
      intelligentServicesApi.middleware,
      companiesApi.middleware,
      partyApplicationApi.middleware
    ),
});

if (window.Cypress) {
  window.store = store;
}

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
